import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { container } from "tsyringe";
import { HelpCenterPresenter } from "../presenters/HelpCenterPresenter";
import {
  buildGitBookIframeSrc,
  clearCachedToken,
  HelpCenterTokenCache,
  isNearExpiry,
  readCachedToken,
  writeCachedToken
} from "@/app/infrastructures/misc/buildHelpCenterSrc";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

const MAX_RETRY = 3;
const RETRY_DELAYS_MS = [2000, 4000, 10000];

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

export interface HelpCenterState {
  iframeSrc: string;
  isLoading: boolean;
  isIframeLoading: boolean;
  isError: boolean;
  errorMessage: string;
  tokenCache: HelpCenterTokenCache | null;
  siteUrl: string;
}

@Module({ namespaced: true, dynamic: true, store, name: "help-center" })
class HelpCenterStore extends VuexModule implements HelpCenterState {
  iframeSrc = "";
  isLoading = false;
  isIframeLoading = false;
  isError = false;
  errorMessage = "";
  tokenCache: HelpCenterTokenCache | null = null;

  get siteUrl(): string {
    return this.tokenCache?.siteUrl ?? "";
  }

  get showLoading(): boolean {
    return this.isLoading || this.isIframeLoading;
  }

  @Mutation
  setIframeSrc(src: string): void {
    this.iframeSrc = src;
  }

  @Mutation
  setLoading(val: boolean): void {
    this.isLoading = val;
  }

  @Mutation
  setIframeLoading(val: boolean): void {
    this.isIframeLoading = val;
  }

  @Mutation
  setError(val: boolean): void {
    this.isError = val;
  }

  @Mutation
  setErrorMessage(msg: string): void {
    this.errorMessage = msg;
  }

  @Mutation
  setTokenCache(data: HelpCenterTokenCache | null): void {
    this.tokenCache = data;
    if (data) {
      writeCachedToken(data);
    } else {
      clearCachedToken();
    }
  }

  @Action
  startActionLoading(): void {
    this.setIframeLoading(true);
    this.syncGlobalLoading();
  }

  @Action
  syncGlobalLoading(): void {
    if (this.isLoading || this.isIframeLoading) {
      MainAppController.showLoading();
    } else {
      MainAppController.closeLoading();
    }
  }

  @Action
  onIframeLoaded(): void {
    this.setIframeLoading(false);
    this.syncGlobalLoading();
  }

  @Action
  clearRefreshTimer(): void {
    if (refreshTimerId !== null) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  }

  @Action
  clearCache(): void {
    this.clearRefreshTimer();
    this.setTokenCache(null);
    this.setIframeSrc("");
    this.setIframeLoading(false);
    this.setError(false);
    this.setErrorMessage("");
  }

  @Action
  scheduleRefresh(expiresAt: string): void {
    this.clearRefreshTimer();
    const delay =
      new Date(expiresAt).getTime() - Date.now() - 5 * 60 * 1000;
    if (delay <= 0) return;
    refreshTimerId = setTimeout(
      () => this.onRefreshTimerFire(),
      delay
    );
  }

  @Action
  async onRefreshTimerFire(): Promise<void> {
    this.clearRefreshTimer();
    const cached = this.tokenCache;
    if (!cached || !isNearExpiry(cached.expiresAt)) return;
    await this.fetchVisitorTokenWithRetry({ keepIframe: true });
  }

  @Action
  async ensureVisitorToken(force = false): Promise<void> {
    const cached = this.tokenCache ?? readCachedToken();
    if (!force && cached && !isNearExpiry(cached.expiresAt)) {
      if (!this.tokenCache) this.setTokenCache(cached);
      this.setIframeSrc(buildGitBookIframeSrc(cached.siteUrl, cached.token));
      this.setIframeLoading(true);
      this.syncGlobalLoading();
      this.scheduleRefresh(cached.expiresAt);
      return;
    }
    this.setLoading(true);
    this.setError(false);
    this.setErrorMessage("");
    this.syncGlobalLoading();
    await this.fetchVisitorTokenWithRetry({ keepIframe: false });
  }

  @Action
  async fetchVisitorTokenWithRetry(opts: {
    keepIframe: boolean;
  }): Promise<void> {
    const presenter = container.resolve(HelpCenterPresenter);
    for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
      const done = await this.tryFetch(presenter, opts.keepIframe, attempt);
      if (done) return;
    }

    this.setLoading(false);
    this.setError(true);
    this.setErrorMessage("An error occurred. Please try again.");
    this.syncGlobalLoading();
  }

  @Action
  async tryFetch(
    presenter: HelpCenterPresenter,
    keepIframe: boolean,
    attempt: number
  ): Promise<boolean> {
    try {
      const result = await presenter.getVisitorToken();
      this.setTokenCache({
        token: result.token,
        siteUrl: result.siteUrl,
        expiresAt: result.expiresAt
      });
      this.setIframeSrc(buildGitBookIframeSrc(result.siteUrl, result.token));
      this.setIframeLoading(true);
      this.clearRefreshTimer();
      this.scheduleRefresh(result.expiresAt);
      this.setLoading(false);
      this.setError(false);
      this.setErrorMessage("");
      this.syncGlobalLoading();
      return true;
    } catch (err) {
      return await this.handleFetchError({ err, keepIframe, attempt });
    }
  }

  @Action
  async handleFetchError(params: {
    err: any;
    keepIframe: boolean;
    attempt: number;
  }): Promise<boolean> {
    const { err, attempt } = params;
    const status = err?.response?.status;
    if (status === 401) {
      this.clearCache();
      this.setLoading(false);
      this.syncGlobalLoading();
      const { AuthController } = await import(
        "@/app/ui/controllers/AuthController"
      );
      AuthController.onLoggingOut();
      return true;
    }
    if (status === 403) {
      this.clearCache();
      this.setLoading(false);
      this.setError(true);
      const msg =
        err?.response?.data?.message?.en ||
        "Access denied";
      this.setErrorMessage(msg);
      this.syncGlobalLoading();
      return true;
    }
    if (attempt < MAX_RETRY) {
      await this.sleep(RETRY_DELAYS_MS[attempt - 1]);
    }
    return false;
  }

  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const HelpCenterController = getModule(HelpCenterStore);
